import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaPen, FaPuzzlePiece, FaKeyboard, FaRandom, FaEnvelope, FaParagraph,
  FaBookOpen, FaMicrophone, FaQuestion, FaHeadphones, FaCommentDots,
  FaLock, FaUnlock, FaArrowLeft, FaPlay, FaFileAlt
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../App.css';

const speakingSections = [
  { id: 'readAloud', name: 'Read Aloud', icon: <FaBookOpen />, description: 'Read the displayed sentence aloud clearly.' },
  { id: 'repeats', name: 'Repeats', icon: <FaMicrophone />, description: 'Listen to a sentence and repeat it exactly.' },
  { id: 'conversations', name: 'Conversations (Part D)', icon: <FaCommentDots />, description: 'Listen to a short 2-person dialogue and give a direct answer.' },
  { id: 'shortAnswer', name: 'Short Answer', icon: <FaQuestion />, description: 'Listen to a question and answer with a word.' },
  { id: 'sentenceBuilds', name: 'Sentence Builds', icon: <FaPuzzlePiece />, description: 'Rearrange jumbled phrases into a sentence.' },
  { id: 'storyRetelling', name: 'Story Retelling', icon: <FaHeadphones />, description: 'Listen to a short story and retell it.' },
  { id: 'openQuestions', name: 'Open Questions', icon: <FaCommentDots />, description: 'Speak for 40 seconds on a general topic.' },
];

const writingSections = [
  { id: 'typing', name: 'Typing Test', icon: <FaKeyboard />, description: 'Test your typing speed and accuracy.' },
  { id: 'sentenceCompletion', name: 'Sentence Completion', icon: <FaPen />, description: 'Complete sentences with the correct words.' },
  { id: 'dictation', name: 'Dictation (Part G)', icon: <FaHeadphones />, description: 'Listen to spoken sentences and type them accurately.' },
  { id: 'fillBlanks', name: 'Fill in the Blanks', icon: <FaPuzzlePiece />, description: 'Fill in the blanks to complete sentences.' },
  { id: 'jumbledWords', name: 'Jumbled Words', icon: <FaRandom />, description: 'Unscramble words to form a correct sentence.' },
  { id: 'emailWriting', name: 'Email Writing', icon: <FaEnvelope />, description: 'Practice writing professional emails.' },
  { id: 'passageReconstruction', name: 'Passage Reconstruction', icon: <FaParagraph />, description: 'Read a passage and then reconstruct it.' },
  { id: 'summaryOpinion', name: 'Summary & Opinion (Part I)', icon: <FaFileAlt />, description: 'Read a passage, summarize it, and write your personal opinion.' },
];

const PracticeHub = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation state defaults
  const initialTab = location.state?.activeTab || 'speaking';
  const initialSection = location.state?.selectedSection || null;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedSection, setSelectedSection] = useState(initialSection);
  const [currency, setCurrency] = useState('INR');

  useEffect(() => {
    const getDetectedCurrency = () => {
      const country = sessionStorage.getItem('ga4_user_country');
      const browserLang = navigator.language || '';
      const sessionLocale = sessionStorage.getItem('versant_locale');
      
      if (window.location.pathname.startsWith('/es') || country === 'Spain' || browserLang.startsWith('es') || sessionLocale === 'es') {
        return 'EUR';
      }
      const highPayingUSDCounteries = ['United States', 'United Kingdom', 'Canada', 'Japan', 'Singapore', 'Australia', 'Brazil', 'South Korea'];
      if (highPayingUSDCounteries.includes(country)) {
        return 'USD';
      }
      if (country === 'India') {
        return 'INR';
      }
      return 'USD';
    };
    setCurrency(getDetectedCurrency());
  }, []);
  
  // User Access State
  const [isFullPremium, setIsFullPremium] = useState(false);
  const [unlockedSections, setUnlockedSections] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeUserDoc = null;

    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        unsubscribeUserDoc = onSnapshot(userRef, (userSnap) => {
          if (userSnap.exists()) {
            const data = userSnap.data();
            const premium = data.paidTests === true || data.hasPaid === true || data.isPremium === true;
            setIsFullPremium(premium);
            setUnlockedSections(data.unlockedSections || {});
          }
          setLoading(false);
        });
      } else {
        setIsFullPremium(false);
        setUnlockedSections({});
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  const isSectionUnlocked = (sectionId) => {
    return isFullPremium || unlockedSections[sectionId] === true;
  };

  const getSectionInfo = (sectionId) => {
    return (
      speakingSections.find(s => s.id === sectionId) ||
      writingSections.find(s => s.id === sectionId)
    );
  };

  const handleStartPractice = (sectionId, setNumber) => {
    // Set 1 is always free
    if (setNumber > 1 && !isSectionUnlocked(sectionId)) {
      navigate('/pricing');
      return;
    }

    if (sectionId === 'conversations' || sectionId === 'dictation' || sectionId === 'summaryOpinion') {
      navigate(`/versant-vept-mock-test/start/${setNumber}?section=${sectionId}`, { 
        state: { testId: setNumber, section: sectionId } 
      });
    } else {
      const isSpeaking = speakingSections.some(s => s.id === sectionId);
      if (isSpeaking) {
        navigate(`/versant-speaking-and-listening-practice-test/start/${sectionId}`, { 
          state: { testId: setNumber } 
        });
      } else {
        navigate(`/versant-practice-exercises/${sectionId}`, { 
          state: { page: setNumber - 1 } 
        });
      }
    }
  };

  const handleUnlockClick = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/pricing');
  };

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      <Helmet>
        <title>Practice Hub | VersantPro</title>
        <meta name="description" content="Practice Versant test sections individually to master your skills." />
      </Helmet>
      <Header page="practiceHub" />

      <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {selectedSection ? (
          /* Set Selection View (1-20 sets) */
          <div>
            <button 
              onClick={() => setSelectedSection(null)}
              className="mb-8 flex items-center gap-2 text-indigo-600 font-bold hover:text-indigo-800 transition-colors"
            >
              <FaArrowLeft /> Back to Sections
            </button>

            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-3xl">
                  {getSectionInfo(selectedSection)?.icon}
                </div>
                <div>
                  <h1 className="text-3xl font-extrabold text-gray-900">{getSectionInfo(selectedSection)?.name}</h1>
                  <p className="text-gray-500 mt-1">{getSectionInfo(selectedSection)?.description}</p>
                </div>
              </div>

              {!isSectionUnlocked(selectedSection) && (
                <button
                  onClick={(e) => handleUnlockClick(e, selectedSection)}
                  className="px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-extrabold rounded-xl shadow-lg active:scale-95 transition flex items-center gap-2 whitespace-nowrap"
                >
                  🔓 Unlock All 20 Sets ({currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'})
                </button>
              )}
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-6">Select a Practice Set (1-20)</h2>
                     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {(() => {
                const sectionInfo = getSectionInfo(selectedSection);
                return Array.from({ length: 20 }, (_, i) => {
                  const setNumber = i + 1;
                  const isLocked = setNumber > 1 && !isSectionUnlocked(selectedSection);

                  return (
                    <div
                      key={setNumber}
                      onClick={() => handleStartPractice(selectedSection, setNumber)}
                      className={`relative p-6 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between h-40 group ${
                        isLocked
                          ? 'bg-[#0f766e]/5 border-[#0f766e]/15 text-[#0f766e] hover:bg-[#0f766e]/10 hover:border-[#0f766e]/25 hover:-translate-y-1 hover:shadow-md'
                          : 'bg-[#0f766e] border-transparent text-white shadow-[0_8px_30px_rgba(15,118,110,0.15)] hover:shadow-[0_20px_40px_-10px_rgba(15,118,110,0.35)] hover:-translate-y-1.5'
                      }`}
                    >
                      {!isLocked && (
                        <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-start">
                        <div>
                          <div className={`text-xl font-black ${isLocked ? 'text-[#0f766e]' : 'text-white'}`}>
                            Set {setNumber}
                          </div>
                          <div className={`text-xs mt-1 font-semibold ${isLocked ? 'text-[#0f766e]/70' : 'text-teal-100/90'}`}>
                            {isLocked ? "Premium Practice Drill" : "Active & Scored"}
                          </div>
                        </div>
                        
                        {sectionInfo && (
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm border ${
                            isLocked 
                              ? 'bg-[#0f766e]/10 border-[#0f766e]/15 text-[#0f766e]/80' 
                              : 'bg-white/15 border-white/10 text-white/90 shadow-inner'
                          }`}>
                            {sectionInfo.icon}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between items-center mt-4">
                        {setNumber === 1 && !isSectionUnlocked(selectedSection) ? (
                          <span className="px-2.5 py-1 bg-white/20 border border-white/10 text-white text-[10px] font-black uppercase rounded-lg tracking-wider">
                            Free
                          </span>
                        ) : (
                          <div></div>
                        )}
                        
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-300 ${
                          isLocked 
                            ? 'bg-[#0f766e]/10 border-[#0f766e]/20 text-[#0f766e]' 
                            : 'bg-white border-transparent text-[#0f766e] group-hover:scale-110 shadow-md'
                        }`}>
                          {isLocked ? <FaLock className="text-xs" /> : <FaPlay className="text-xs ml-0.5" />}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        ) : (
          /* Grid list of Sections (Tabs) */
          <div>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-4">Practice Hub</h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Sharpen specific parts of the Versant assessment. Choose any section to access 20 dedicated sets.
              </p>
            </div>

            {/* Tab Switched Grid Header */}
            <div className="flex justify-center mb-12">
              <div className="bg-gray-100 p-1.5 rounded-2xl flex gap-2">
                <button
                  onClick={() => setActiveTab('speaking')}
                  className={`px-6 py-3 font-bold rounded-xl transition-all duration-200 text-sm ${
                    activeTab === 'speaking'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  🎙️ Speaking Sections
                </button>
                <button
                  onClick={() => setActiveTab('writing')}
                  className={`px-6 py-3 font-bold rounded-xl transition-all duration-200 text-sm ${
                    activeTab === 'writing'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  ✍️ Reading & Writing Sections
                </button>
              </div>
            </div>

            {/* Sections Grid Rendering */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeTab === 'speaking' ? speakingSections : writingSections).map((section) => {
                const unlocked = isSectionUnlocked(section.id);
                const isSpeaking = activeTab === 'speaking';

                return (
                  <div
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`group relative rounded-[1.5rem] p-6 flex flex-col justify-between h-[220px] transition-all duration-500 overflow-hidden cursor-pointer ${
                      isSpeaking 
                        ? 'bg-[#0f766e] border border-teal-600 hover:shadow-[0_20px_50px_rgba(15,118,110,0.3)] hover:-translate-y-2' 
                        : 'bg-[#065f46] border border-emerald-500 hover:shadow-[0_20px_50px_rgba(6,95,70,0.3)] hover:-translate-y-2'
                    }`}
                  >
                    {/* Premium Glassmorphic reflection shine */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-white/20 text-white flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                          {section.icon}
                        </div>
                        <div className="flex items-center gap-2">
                          {unlocked ? (
                            <span className={`flex items-center gap-1 text-xs font-bold bg-white/20 px-2.5 py-1 rounded-full border border-white/10 ${isSpeaking ? 'text-teal-200' : 'text-emerald-200'}`}>
                              <FaUnlock className="text-[10px]" /> Unlocked
                            </span>
                          ) : (
                            <button
                              onClick={(e) => handleUnlockClick(e, section.id)}
                              className={`flex items-center gap-1 text-xs font-extrabold bg-white px-2.5 py-1 rounded-full shadow-md active:scale-95 hover:-translate-y-[1px] transition-all duration-300 ${
                                isSpeaking 
                                  ? 'text-[#0f766e] hover:bg-teal-50' 
                                  : 'text-[#065f46] hover:bg-emerald-50'
                              }`}
                            >
                              <FaLock className="text-[10px]" /> Buy {currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'}
                            </button>
                          )}
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-white mb-1.5 tracking-tight">
                        {section.name}
                      </h3>
                      <p className={`text-xs leading-relaxed font-light ${isSpeaking ? 'text-teal-100/90' : 'text-emerald-100/90'}`}>
                        {section.description}
                      </p>
                    </div>

                    <div className="border-t border-white/10 pt-3.5 flex justify-between items-center text-xs font-bold text-white uppercase tracking-wider relative z-10">
                      <span>Select Practice Set</span>
                      <FaPlay className="text-[10px] text-white group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default PracticeHub;